import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AuditEntry,
  PlatformAuditEntry,
  PlatformAuditPage,
  TenantDetail,
} from '@up2d8/contracts';
import { AdminPage, Badge, FilterSelect, Note, PageHead, PersonCell } from '../admin-kit';
import { useSession } from '../../shared/session';
import { AuditTrail, SystemActor } from './audit-trail';

/**
 * De soorten op het platform: het begin van de eventnaam. Smaller dan in een bedrijf, want het
 * platform doet minder: het merk, de bedrijven zelf, hun functies en huisstijl, en uitnodigingen.
 * Alles van `tenants.` samen zou het merk tussen de bedrijven verstoppen, en het merk is precies
 * wat in geen ander spoor staat; daarom per tweede deel.
 */
const SOORTEN = [
  ['tenants.platform_brand.', 'kind_brand'],
  ['tenants.tenant.', 'kind_tenant'],
  ['tenants.feature.', 'kind_feature'],
  ['tenants.theme.', 'kind_theme'],
  ['identity.', 'kind_identity'],
] as const;

/** Op het platform heeft een platformbeheerder een naam; een persoon van een klant komt hier niet. */
const NAMED: AuditEntry['actorType'][] = ['platform_admin', 'person'];

/**
 * `/master/audit`: het auditspoor van het platform (AUD-1, ADR-0088).
 *
 * Hetzelfde scherm als `/admin/audit` (`AuditTrail`), met twee verschillen. "Wie" is hier de naam
 * van de platformbeheerder en niet de badge "UP2D8-beheer" - op het platform is dat iedereen. En er
 * is een kolom en een filter Bedrijf: een regel gaat over één bedrijf, of over heel het platform
 * (het merk). Geen export: het recht ervoor bestaat nog niet, en het spoor is klein.
 */
export function PlatformAuditPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const allowed = has('platform.audit.view');
  const [tenantId, setTenantId] = useState('');

  // De bedrijven voor het filter: dezelfde lijst als Bedrijven, tot honderd.
  const tenants = useQuery({
    queryKey: ['platform-tenants', 'audit-filter'],
    enabled: allowed,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants', {
        params: { query: { limit: 100 } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: TenantDetail[] }).items;
    },
  });

  if (!allowed) {
    return (
      <AdminPage>
        <PageHead title={t('beheer.platformAudit.title')} sub={t('beheer.platformAudit.sub')} />
        <Note tone="attention" role="alert" testId="platform-audit-no-right">
          {t('beheer.platformAudit.noRight')}
        </Note>
      </AdminPage>
    );
  }

  return (
    <AuditTrail<PlatformAuditEntry>
      queryKey={['platform-audit']}
      fetchPage={async (query, cursor) => {
        const { data, error } = await api.GET('/api/v1/platform/audit', {
          params: { query: { ...query, ...(cursor ? { cursor } : {}) } } as never,
        });
        if (error || !data) throw error ?? new Error('Ophalen mislukt');
        return data as PlatformAuditPage;
      }}
      title={t('beheer.platformAudit.title')}
      sub={t('beheer.platformAudit.sub')}
      kinds={SOORTEN.map(([value, key]) => ({ value, label: t(`beheer.platformAudit.${key}`) }))}
      actorTypes={NAMED}
      renderActor={(entry) =>
        entry.actorType === 'system' ? <SystemActor /> : <PersonCell name={entry.actorName} />
      }
      extraFilters={
        <FilterSelect
          label={t('beheer.platformAudit.company')}
          allLabel={t('beheer.platformAudit.allCompanies')}
          value={tenantId}
          onChange={setTenantId}
          testId="audit-tenant"
          options={(tenants.data ?? [])
            .map((tenant) => ({ value: tenant.id, label: tenant.name }))
            .sort((a, b) => a.label.localeCompare(b.label))}
        />
      }
      extraQuery={{ tenantId }}
      columns={[
        {
          key: 'tenant',
          label: t('beheer.platformAudit.company'),
          cell: (entry) =>
            entry.tenantId ? (
              (entry.tenantName ?? entry.tenantId)
            ) : (
              <Badge tone="navy">{t('beheer.platformAudit.wholePlatform')}</Badge>
            ),
        },
      ]}
      emptyHint={t('beheer.platformAudit.emptyHint')}
    />
  );
}
